import * as THREE from 'three';
import { type FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { type ThreeEvent } from '@react-three/fiber';
import debounce from 'debounce';
import type {
  InstanceProps,
  InstancedObjectMeshData,
  Number3,
  ImageAnchorStyle,
  ImageAnchorStyles,
  ImageProps,
  ImageSetExtendedProps,
} from '../../types';
import { InstancedObject } from '../InstancedObject';
import { ImageType } from '../Enums';
import { cameraEulerFromAbyssRotation } from '../Util';
import { useAbyssViewerContext } from '../AbyssViewerContext';
import { RENDER_ORDER_IMAGE_ANCHOR } from '../RenderOrder';
import { ImageBillboard } from '../ImageBillboard';

interface FinalImageAnchorStyle {
  fillColor: number;
  fillOpacity: number;
  linesColor: number;
  linesOpacity: number;
  size: number;
}

interface FinalImageAnchorStyles {
  normal: FinalImageAnchorStyle;
  highlighted: FinalImageAnchorStyle;
  viewModeNormal: FinalImageAnchorStyle;
  viewModeHighlighted: FinalImageAnchorStyle;
}

const defaultStyles = {
  normal: {
    fillColor: 0x7c94ff,
    fillOpacity: 0.5,
    linesColor: 0x7c94ff,
    linesOpacity: 0.5,
    size: 0.15,
  },
  highlighted: {
    fillColor: 0xff3d3d,
    fillOpacity: 0.6,
    linesColor: 0xff3d3d,
    linesOpacity: 0.6,
    size: 0.15,
  },
  viewModeNormal: {
    fillColor: 0x7c94ff,
    fillOpacity: 0.5,
    linesColor: 0x7c94ff,
    linesOpacity: 0.5,
    size: 0.075,
  },
  viewModeHighlighted: {
    fillColor: 0xff3d3d,
    fillOpacity: 0.6,
    linesColor: 0xff3d3d,
    linesOpacity: 0.6,
    size: 0.075,
  },
};

const getStyleForMode = (
  styles: ImageAnchorStyles | undefined,
  isImageViewMode: boolean,
  isHighlighted: boolean
): ImageAnchorStyle | undefined =>
  isImageViewMode
    ? isHighlighted
      ? styles?.viewModeHighlighted
      : styles?.viewModeNormal
    : isHighlighted
      ? styles?.highlighted
      : styles?.normal;

const getFinalStyleForMode = (
  styles: FinalImageAnchorStyles,
  isImageViewMode: boolean,
  isHighlighted: boolean
): FinalImageAnchorStyle =>
  isImageViewMode
    ? isHighlighted
      ? styles?.viewModeHighlighted
      : styles?.viewModeNormal
    : isHighlighted
      ? styles?.highlighted
      : styles?.normal;

const getActualStyle = (
  defaultStyles: FinalImageAnchorStyles,
  groupStyles: ImageAnchorStyles | undefined,
  imageStyles: ImageAnchorStyles | undefined,
  isImageViewMode: boolean,
  isHighlighted: boolean
): FinalImageAnchorStyle => {
  const defaultStyle = getFinalStyleForMode(defaultStyles, isImageViewMode, isHighlighted);
  const groupStyle = getStyleForMode(groupStyles, isImageViewMode, isHighlighted);
  const imageStyle = getStyleForMode(imageStyles, isImageViewMode, isHighlighted);
  return {
    ...defaultStyle,
    ...groupStyle,
    ...imageStyle,
  };
};

const calcImageScale = (image: ImageProps, style: FinalImageAnchorStyle): Number3 => {
  const { width, height, focalLength } = image;
  const y_ratio = height / width;
  const z_ratio = (2 * focalLength) / width;
  const scale = style.size;
  return [scale, scale * y_ratio, scale * z_ratio];
};

export const InstancedImageSet: FC<ImageSetExtendedProps> = memo(
  ({
    id,
    name,
    images,
    styles: groupStyles,
    isImageViewMode,
    isVisible,
    visibilityBox,
    currentImage,
    setNextCurrentImage,
  }) => {
    const { sharedGeometries } = useAbyssViewerContext();
    const { anchorLine, anchorPlane } = sharedGeometries ?? {};

    const [hoveredImage, setHoveredImage] = useState<ImageProps>();

    const isInsideVisibilityBox = useCallback(
      (imageProps: ImageProps) => {
        return visibilityBox.checkIsInside(imageProps.position);
      },
      [visibilityBox]
    );

    const shouldHandleClick = useCallback((event: ThreeEvent<PointerEvent>) => {
      const { altKey, ctrlKey, metaKey, shiftKey, button } = event.nativeEvent;
      const isLeftButton = button === 0;
      // double-click is not detected in useOnClick handler
      // const isDoubleClick = event.nativeEvent.detail === 2;
      if (
        !isLeftButton ||
        // isDoubleClick ||
        altKey ||
        ctrlKey ||
        metaKey ||
        shiftKey
        // !isSphericalClosestToCursor(event)
      ) {
        return false;
      }
      return true;
    }, []);

    const enterSphericalMode = useCallback(
      (image: ImageProps) => {
        setHoveredImage(undefined);
        setNextCurrentImage({
          imageType: ImageType.Rectilinear,
          ...image,
        });
      },
      [setNextCurrentImage]
    );

    const debouncedEnterSphericalMode = debounce(enterSphericalMode, 300);

    const onClick = useCallback(
      (event: ThreeEvent<PointerEvent>, image: ImageProps) => {
        // console.log('onClick', event, image);
        event.stopPropagation();
        debouncedEnterSphericalMode(image);
      },
      [debouncedEnterSphericalMode]
    );

    const anchorInstances: InstanceProps[] = useMemo(() => {
      return images
        .filter((imageProps) => (isVisible ?? true) && isInsideVisibilityBox(imageProps))
        .map((image) => {
          const isCurrentImage = currentImage && currentImage.name === image.name;
          const { position, rotation, styles: imageStyles } = image;

          const adjustedRotation = cameraEulerFromAbyssRotation(rotation);

          const normalStyle = getActualStyle(
            defaultStyles,
            groupStyles,
            imageStyles,
            isImageViewMode,
            false
          );

          const highlightedStyle = getActualStyle(
            defaultStyles,
            groupStyles,
            imageStyles,
            isImageViewMode,
            true
          );

          const normalScale = calcImageScale(image, normalStyle);
          const highlightedScale = calcImageScale(image, highlightedStyle);

          return {
            position,
            rotation: [adjustedRotation.x, adjustedRotation.y, adjustedRotation.z],
            scale: isCurrentImage ? 0 : normalScale,
            // TODO: support multiple colours per instance
            color: normalStyle.fillColor,
            onClick: (event: ThreeEvent<PointerEvent>) => onClick(event, image),
            onPointerOver: () => {
              setHoveredImage(image);
              return {
                color: highlightedStyle.fillColor,
                scale: highlightedScale,
              };
            },
            onPointerOut: () => {
              setHoveredImage(undefined);
            },
          };
        });
    }, [
      images,
      groupStyles,
      isVisible,
      isInsideVisibilityBox,
      isImageViewMode,
      currentImage,
      onClick,
    ]);

    const [planeMaterial, lineMaterial, highlightedPlaneMaterial, highlightedLineMaterial] =
      useMemo(() => {
        return [
          new THREE.MeshBasicMaterial({
            transparent: true,
            side: THREE.DoubleSide,
          }),
          new THREE.MeshBasicMaterial({
            wireframe: true,
            side: THREE.DoubleSide,
          }),
          new THREE.MeshBasicMaterial({
            transparent: true,
            side: THREE.DoubleSide,
          }),
          new THREE.MeshBasicMaterial({
            wireframe: true,
            side: THREE.DoubleSide,
          }),
        ];
      }, []);

    useEffect(() => {
      const isHighlighted = false;
      const defaultStyle = getFinalStyleForMode(defaultStyles, isImageViewMode, isHighlighted);
      const groupStyle = getStyleForMode(groupStyles, isImageViewMode, isHighlighted);

      const { fillOpacity, linesOpacity } = { ...defaultStyle, ...groupStyle };
      // each instance will have it's own color
      // material.color.setHex(color);
      planeMaterial.opacity = fillOpacity;
      planeMaterial.needsUpdate = true;
      lineMaterial.opacity = linesOpacity;
      lineMaterial.needsUpdate = true;
    }, [planeMaterial, lineMaterial, groupStyles, isImageViewMode]);

    useEffect(() => {
      if (!planeMaterial) return () => {};
      return () => {
        planeMaterial.dispose();
        lineMaterial.dispose();
      };
    }, [planeMaterial, lineMaterial]);

    useEffect(() => {
      if (!hoveredImage) return;
      const isHighlighted = true;
      const defaultStyle = getFinalStyleForMode(defaultStyles, isImageViewMode, isHighlighted);
      const groupStyle = getStyleForMode(groupStyles, isImageViewMode, isHighlighted);
      const imageStyle = getStyleForMode(hoveredImage.styles, isImageViewMode, isHighlighted);

      const { fillColor, fillOpacity, linesColor, linesOpacity } = {
        ...defaultStyle,
        ...groupStyle,
        ...imageStyle,
      };

      highlightedPlaneMaterial.color.setHex(fillColor);
      highlightedPlaneMaterial.opacity = fillOpacity;
      highlightedPlaneMaterial.needsUpdate = true;

      highlightedLineMaterial.color.setHex(linesColor);
      highlightedLineMaterial.opacity = linesOpacity;
      highlightedLineMaterial.needsUpdate = true;
    }, [
      highlightedPlaneMaterial,
      highlightedLineMaterial,
      groupStyles,
      isImageViewMode,
      hoveredImage,
    ]);

    useEffect(() => {
      if (!highlightedPlaneMaterial) return () => {};
      return () => {
        highlightedPlaneMaterial.dispose();
        highlightedLineMaterial.dispose();
      };
    }, [highlightedPlaneMaterial, highlightedLineMaterial]);

    const meshes: InstancedObjectMeshData[] = useMemo(() => {
      if (!anchorLine || !anchorPlane) return [];
      const renderOrder = RENDER_ORDER_IMAGE_ANCHOR;
      return [
        { geometry: anchorLine, material: lineMaterial, renderOrder },
        { geometry: anchorPlane, material: planeMaterial, renderOrder },
      ];
    }, [anchorLine, anchorPlane, planeMaterial, lineMaterial]);

    const hoveredMeshes: InstancedObjectMeshData[] = useMemo(() => {
      if (!anchorLine || !anchorPlane) return [];
      return [
        { geometry: anchorLine, material: highlightedLineMaterial },
        { geometry: anchorPlane, material: highlightedPlaneMaterial },
      ];
    }, [anchorLine, anchorPlane, highlightedPlaneMaterial, highlightedLineMaterial]);

    const instancedObject = useMemo(() => {
      return (
        <InstancedObject
          id={id ?? name}
          meshes={meshes}
          hoveredMeshes={hoveredMeshes}
          instances={anchorInstances}
          shouldHandleClick={shouldHandleClick}
        />
      );
    }, [id, name, meshes, hoveredMeshes, anchorInstances, shouldHandleClick]);

    const hoveredAnchor = useMemo(
      () => (
        <>
          {hoveredImage && hoveredImage?.name !== currentImage?.name && (
            <ImageBillboard position={hoveredImage.position} name={hoveredImage.name} />
          )}
        </>
      ),
      [hoveredImage, currentImage]
    );

    return (
      <>
        {instancedObject}
        {hoveredAnchor}
      </>
    );
  }
);
